import React, { useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from '@frontend/shadcn/components/ui/drawer';
import { XmarkIcon } from '@revfluence/fresh-icons/solid';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@frontend/shadcn/components/ui/avatar';
import { cn } from '@frontend/shadcn/lib/utils';
import { useGetAllProjectsQuery, useGetAllWorkletsForParentSpecQuery } from '@frontend/app/hooks';
import { format } from 'date-fns';
import { GetAllProjectsQuery_projects } from '@frontend/app/queries/types/GetAllProjectsQuery';
import { keyBy } from 'lodash';
import { useCampaign } from '../CampaignContext';

export interface DuplicateCampaignDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CampaignCardProps {
  project: GetAllProjectsQuery_projects;
  isSelected: boolean;
  onClick: () => void;
}

const CampaignCard = ({ project, isSelected, onClick }: CampaignCardProps) => (
  <div
    className={cn(
      'flex items-center justify-between p-4 bg-white border border-grey-3 rounded-2xl cursor-pointer',
      isSelected && 'border-primary bg-gray-50 shadow-md'
    )}
    onClick={onClick}
  >
    <div className="flex items-center gap-4">
      <Avatar className="h-11 w-11 rounded-lg">
        <AvatarImage src={project.splashImageUrl} alt={project.title} className="object-cover rounded-lg" />
        <AvatarFallback className="rounded-lg bg-grey-2">
          {project.title.substring(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col gap-1">
        <h4 className="text-sm font-medium text-primary">{project.title}</h4>
        {project?.programMetadata?.startDate && (
          <p className="text-xs text-subtitle">
            {format(new Date(project.programMetadata.startDate), 'MMM d, yyyy')}
            {project?.programMetadata?.endDate && (
              <> - {format(new Date(project.programMetadata.endDate), 'MMM d, yyyy')}</>
            )}
          </p>
        )}
      </div>
    </div>
    <Button variant="outline" size="sm" className="h-8 border-grey-3 text-sm font-medium">
      Duplicate
    </Button>
  </div>
);

interface CampaignSectionProps {
  title: string;
  projects: GetAllProjectsQuery_projects[];
  isExpanded: boolean;
  onToggle: () => void;
  selectedProjectId?: number;
  onProjectSelect: (project: GetAllProjectsQuery_projects) => void;
}

const CampaignSection = ({
  title,
  projects,
  isExpanded,
  onToggle,
  selectedProjectId,
  onProjectSelect
}: CampaignSectionProps) => (
  <div className="flex flex-col gap-4">
    <div className="flex justify-between items-center cursor-pointer" onClick={onToggle}>
      <h3 className="text-sm font-medium text-primary">{title}</h3>
      <ChevronDownIcon className={cn('h-4 w-4 text-primary transition-transform', !isExpanded && '-rotate-90')} />
    </div>
    {isExpanded && (
      <div className="flex flex-col gap-4">
        {projects?.map((project) => (
          <CampaignCard
            key={project.id}
            project={project}
            isSelected={selectedProjectId === project.id}
            onClick={() => onProjectSelect(project)}
          />
        ))}
      </div>
    )}
  </div>
);

export const DuplicateCampaignDrawer: React.FC<DuplicateCampaignDrawerProps> = ({ isOpen, onOpenChange }) => {
  const {
    duplicateCampaignId,
    setAllCampaignData,
    ...state
  } = useCampaign();
  const { data: projectsData } = useGetAllProjectsQuery();
  const [searchQuery, setSearchQuery] = useState('');
  const [isActiveExpanded, setIsActiveExpanded] = useState(true);
  const [isArchivedExpanded, setIsArchivedExpanded] = useState(true);

  const { refetch: refetchWorklets } = useGetAllWorkletsForParentSpecQuery({
    skip: true,
  });

  const activeProjects = React.useMemo(
    () => projectsData?.projects
      .filter((project) => project.status === 'active')
      .filter((project) => project.title.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => a.title.localeCompare(b.title)),
    [projectsData, searchQuery]
  );

  const archivedProjects = React.useMemo(
    () => projectsData?.projects
      .filter((project) => project.status === 'archived')
      .filter((project) => project.title.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort((a, b) => a.title.localeCompare(b.title)),
    [projectsData, searchQuery]
  );

  const handleProjectSelect = async (project: GetAllProjectsQuery_projects) => {
    const { data: workletsData } = await refetchWorklets({
      specKey: project.specKey,
    });

    const workletsById = keyBy(workletsData?.worklets, 'specKey');
    setAllCampaignData({
      ...state,
      campaignName: `${project.title} (Copy)`,
      campaignImage: project.splashImageUrl || '',
      campaignIcon: null,
      campaignDescription: project.description || '',
      listToMarketplace: project.published || false,
      contentReviewEnabled: project.gcrEnabled || false,
      contentReviewers: project.gcrApproverIds || [],
      templateName: project.templateName || '',
      worklets: project.workletSpecKeys?.map((specKey) => workletsById[specKey]) || [],
      programId: project.id,
      startDate: project.programMetadata?.startDate ? new Date(project.programMetadata.startDate) : undefined,
      endDate: project.programMetadata?.endDate ? new Date(project.programMetadata.endDate) : undefined,
      budget: project.programMetadata?.budget,
      budgetTimePeriod: project.programMetadata?.budgetTimePeriod,
      salesGoal: project.programMetadata?.salesGoal,
      salesGoalTimePeriod: project.programMetadata?.salesGoalTimePeriod,
      creatorCount: project.programMetadata?.creatorCount,
      creatorsTimePeriod: project.programMetadata?.creatorsTimePeriod,
      creatorDescription: project.programMetadata?.creatorDescription,
      creatorPreference: project.programMetadata?.creatorPreference,
      contentPrefPostTypes: project.programMetadata?.contentPrefPostTypes,
      compensationMethod: project.programMetadata?.compensationMethod,
      commissionValue: project.programMetadata?.commissionValue,
      commissionValueType: project.programMetadata?.commissionValueType,
      discountValue: project.programMetadata?.discountValue,
      discountType: project.programMetadata?.discountType,
      discountValueType: project.programMetadata?.discountValueType,
      isFlexibleSpec: project.isFlexibleSpec,
      duplicateCampaignId: project.id,
      hasUnsavedChanges: true,
      currentStep: 2,
    });
    onOpenChange(false);
  };

  React.useEffect(() => {
    if (duplicateCampaignId) {
      onOpenChange(true);
    }
  }, [duplicateCampaignId, onOpenChange]);

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange} direction="right">
      <DrawerContent
        className={cn(
          'fixed left-auto inset-y-0 mt-0 right-0 z-[9999] flex h-full w-[576px] flex-col border-l bg-white rounded-none p-0',
          'duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          'top-[var(--header-height,0px)] h-[calc(100vh-var(--header-height,0px))]',
          'shadow-[0_2px_6px_-4px_rgba(0,0,0,0.12),0_4px_8px_0px_rgba(93,105,116,0.08),0_8px_24px_8px_rgba(93,105,116,0.05)]'
        )}
      >
        <DrawerHeader className="flex justify-between items-center border-b border-grey-3 px-6 py-4">
          <DrawerTitle className="text-primary font-medium text-base">Duplicate a Campaign</DrawerTitle>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="headerIcon"
              className="border border-transparent hover:border-grey-6 hover:bg-transparent"
            >
              <XmarkIcon className="h-4 w-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className="flex flex-col gap-6 px-6 py-4 overflow-y-auto">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
            <Input
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-8 border-grey-3 rounded-lg text-sm"
            />
          </div>

          <div className="flex flex-col gap-6">
            <CampaignSection
              title="Active Campaigns"
              projects={activeProjects || []}
              isExpanded={isActiveExpanded}
              onToggle={() => setIsActiveExpanded(!isActiveExpanded)}
              selectedProjectId={duplicateCampaignId}
              onProjectSelect={handleProjectSelect}
            />
            <CampaignSection
              title="Archived Campaigns"
              projects={archivedProjects || []}
              isExpanded={isArchivedExpanded}
              onToggle={() => setIsArchivedExpanded(!isArchivedExpanded)}
              selectedProjectId={duplicateCampaignId}
              onProjectSelect={handleProjectSelect}
            />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
